/**
 * Authentification Cognito, en SRP.
 *
 * `amazon-cognito-identity-js` plutôt qu'AWS Amplify : le pool n'autorise que `USER_SRP_AUTH` et le
 * rafraîchissement (`docs/DECISIONS.md` D4), il n'y a ni Hosted UI ni flux OAuth à gérer, et Amplify
 * apporterait une couche de configuration et un poids de bundle pour un besoin que cette
 * bibliothèque couvre entièrement (`docs/agent-spec/24-PROJECT-PHILOSOPHY.md`, peu de dépendances).
 *
 * Le jeton est conservé par la bibliothèque dans le `localStorage`, ce qui permet de rester connecté
 * d'un onglet à l'autre. Rien d'autre n'y est écrit par l'application.
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

import type { Profil } from "../api/types";

/**
 * Du plus restrictif au plus large. Miroir de `_ORDRE_RESTRICTION` dans `common/auth.py`.
 *
 * Déclaré explicitement, et non déduit en inversant `PROFILS` : cette dernière liste est un ordre
 * d'affichage, et la réordonner un jour changerait silencieusement une règle de sécurité. Le backend
 * a fait le même choix, pour la même raison.
 */
const ORDRE_RESTRICTION: readonly Profil[] = [
  "support",
  "air-tahiti",
  "operationnel-totalenergies",
  "admin-totalenergies",
];

export type Identite = {
  sub: string;
  email: string;
  profil: Profil;
  /** Renseigné pour un utilisateur cloisonné ; `null` pour les profils TotalEnergies et Support. */
  clientId: string | null;
};

export class MotDePasseAChanger extends Error {
  constructor(readonly utilisateur: CognitoUser) {
    super("Un nouveau mot de passe est requis pour cette première connexion.");
    this.name = "MotDePasseAChanger";
  }
}

let pool: CognitoUserPool | null = null;

function poolCognito(): CognitoUserPool {
  if (pool) return pool;
  const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!UserPoolId || !ClientId) {
    throw new Error(
      "VITE_COGNITO_USER_POOL_ID ou VITE_COGNITO_CLIENT_ID manquant : voir `.env.example`.",
    );
  }
  pool = new CognitoUserPool({ UserPoolId, ClientId });
  return pool;
}

/**
 * Profil retenu quand un compte appartient à plusieurs groupes : **le plus restrictif**.
 *
 * Le cas ne devrait pas se produire — l'administration n'affecte qu'un groupe — mais s'il se produit,
 * accorder le plus permissif serait un défaut de sécurité silencieux. Le serveur applique la même
 * règle (`common/auth.py`) : c'est lui qui fait foi, celle-ci ne sert qu'à afficher un menu cohérent.
 */
export function profilLePlusRestrictif(groupes: string[]): Profil | null {
  for (const candidat of ORDRE_RESTRICTION) {
    if (groupes.includes(candidat)) return candidat;
  }
  return null;
}

function identiteDepuisSession(session: CognitoUserSession): Identite {
  const revendications = session.getIdToken().decodePayload() as Record<string, unknown>;
  const groupes = (revendications["cognito:groups"] as string[] | undefined) ?? [];
  const profil = profilLePlusRestrictif(groupes);
  if (!profil) {
    throw new Error(
      "Ce compte n'appartient à aucun groupe connu. Contacter un administrateur TotalEnergies.",
    );
  }
  return {
    sub: String(revendications.sub),
    email: String(revendications.email ?? ""),
    profil,
    clientId: (revendications["custom:client_id"] as string | undefined) ?? null,
  };
}

export function seConnecter(email: string, motDePasse: string): Promise<Identite> {
  const utilisateur = new CognitoUser({ Username: email, Pool: poolCognito() });
  const details = new AuthenticationDetails({ Username: email, Password: motDePasse });

  return new Promise((resoudre, rejeter) => {
    utilisateur.authenticateUser(details, {
      onSuccess: (session) => resoudre(identiteDepuisSession(session)),
      onFailure: (erreur: Error) => rejeter(erreur),
      // Cognito impose le changement de mot de passe à la première connexion, l'invitation portant un
      // mot de passe temporaire (`06-authentification-et-droits.md`). Ce n'est pas un échec : c'est
      // une étape du parcours, et l'écran de connexion la présente comme telle.
      newPasswordRequired: () => rejeter(new MotDePasseAChanger(utilisateur)),
    });
  });
}

export function definirNouveauMotDePasse(
  utilisateur: CognitoUser,
  nouveauMotDePasse: string,
): Promise<Identite> {
  return new Promise((resoudre, rejeter) => {
    utilisateur.completeNewPasswordChallenge(nouveauMotDePasse, {}, {
      onSuccess: (session: CognitoUserSession) => resoudre(identiteDepuisSession(session)),
      onFailure: (erreur: Error) => rejeter(erreur),
    });
  });
}

/** Session en cours, rafraîchie si besoin. `null` si personne n'est connecté. */
export function sessionCourante(): Promise<CognitoUserSession | null> {
  const utilisateur = poolCognito().getCurrentUser();
  if (!utilisateur) return Promise.resolve(null);

  return new Promise((resoudre) => {
    utilisateur.getSession((erreur: Error | null, session: CognitoUserSession | null) => {
      resoudre(erreur || !session?.isValid() ? null : session);
    });
  });
}

export async function identiteCourante(): Promise<Identite | null> {
  const session = await sessionCourante();
  return session ? identiteDepuisSession(session) : null;
}

/**
 * Jeton d'identité à joindre aux appels d'API.
 *
 * C'est le jeton d'**identité** et non celui d'accès : l'autorisateur JWT d'API Gateway est configuré
 * sur lui, et c'est lui qui porte `cognito:groups` et `custom:client_id`, dont dépendent les droits
 * et le cloisonnement côté serveur.
 */
export async function jetonCourant(): Promise<string | null> {
  const session = await sessionCourante();
  return session ? session.getIdToken().getJwtToken() : null;
}

export function seDeconnecter(): void {
  poolCognito().getCurrentUser()?.signOut();
}
